import { create, useStore } from "zustand";

// Simple and safe wrapper around loading and saving objects in localStorage.
const Storage = {
    loadObject<T>(key: string, fallback?: T): T | undefined {
        try {
            const stored = localStorage.getItem(key);
            return stored ? JSON.parse(stored) : fallback;
        } catch (e) {
            console.error(`Failed to load object from localStorage key ${key}`, e);
            return fallback;
        }
    },
    saveObject<T>(key: string, value: T) {
        try {
            localStorage.setItem(key, JSON.stringify(value));
        } catch (error) {
            console.error(`Failed to save to localStorage key "${key}":`, error);
        }
    },
};

type StoreConfig = {
    persistKey?: string;
};

/**
 * Centralized helper for creating and interacting with a zustand store.
 * Pass a peristKey into the config options to save the store state to localStorage.
 *
 * --- How to use ---
 *
 * - Anywhere
 * const store = createStore({ x: 1, y: 1 })
 *
 * - In a component
 * const { x, y } = store.use() <-- these values are now reactive like useState
 * store.update({x: x + 1, y: y + 1}) <-- update them like this
 *
 * You can also use store.update outside of React components
 *
 */
export function createStore<T extends object>(initial?: T, config: StoreConfig = {}) {
    let initialState = initial ?? ({} as T);

    const store = create<T>(() => initialState);

    // If a persistKey was passed in, safely load the store from localStorage with the initialState
    // being the fallback.
    const { persistKey } = config;

    if (persistKey) {
        const stored = Storage.loadObject(persistKey, initialState);
        if (stored) {
            // replace = true to not trigger an immediate subscription update below
            store.setState(stored, true);
        }
        // Setup subscriber to write to localStorage when the state is changed.
        store.subscribe(state => {
            Storage.saveObject(persistKey, state);
        });
    }

    // These are purely for convenience.
    return Object.assign(store, {
        // The current values in the store at the time of the function call.
        current: () => store.getState(),

        // Wrapper around setState (this was before realizing setState handled partial state updates correctly)
        update: (partial: Partial<T>) => {
            // NOTE: if you use an array or object inside the store and update it like this it will overwrite the entire key.
            // For that you would want to do store.setState(prevState => { return {...prevState, }) where you specifically
            // update that array/object with access to the previous state.

            store.setState(partial);
        },

        // Functionally the same as update with the exception that it clears all other state that is not specified.
        // This effectively resets the store except for the partial state that was passed in.
        explicitUpdate: (partial: Partial<T>) => {
            store.setState(
                {
                    ...initialState,
                    ...partial,
                },
                true,
            );
        },

        // Reset the store back to the initial state. The true is replace = true which does not to a partial update
        // it replaces the entire store with the object passed in.
        reset: () => {
            store.setState(initialState ?? ({} as T), true);
        },

        // This is a convenience method to listen to a single value in the store's state updates.
        // ex: const x = store.use(state => state.x) -- this would only receive updates when x is updated in the store.
        // in contrast: const { x } = store.use() is going to update/cause re-render when any value in the store is updated.

        // NOTE: Only use the optional selector like this for single value selections. Returning an object like store.use(s => { x: s.x, y: s.y }) will cause problems.
        use: <U = T>(selector?: (state: T) => U) => {
            if (selector) {
                return useStore(store, selector);
            }
            return useStore(store, state => state as unknown as U);
        },
    });
}
